import React, {useCallback, useEffect, useState} from 'react';
import {useQuery} from '@tanstack/react-query';
import {Controller, useForm, useWatch} from 'react-hook-form';
import {z} from 'zod';
import {zodResolver} from '@hookform/resolvers/zod';
import {
  Button,
  FormControlLabel,
  FormLabel,
  MenuItem,
  Select,
  Stack,
  Switch,
  Typography,
} from '@mui/material';
import {LoadingButton} from '@mui/lab';
import AddIcon from '@mui/icons-material/Add';
import {StyleSx, Sx} from 'app/types/common';
import {UserApi, UserMapper} from 'app/components/entities/user';
import {BillingMapper} from 'app/services/mappers/BillingMapper';
import {CreateGroupDialog} from 'app/components/dialogs/CreateGroupDialog/CreateGroupDialog';
import {DeviceGroupApiService} from 'app/services/api/deviceGroup/DeviceGroupApiService';
import {Team} from 'app/components/entities/team';
import {CloudApi, usePairedStatsQuery} from 'app/components/entities/cloud';
import {EdgeBillingApi} from 'app/components/entities/billing/edge';
import {useEdgeGroups} from 'app/components/features/edge';
import {SeatsSummary, ValidationMessage, useCheckCode} from 'app/components/features/pairing';
import {router} from 'app/router/main';

const scheme = z.object({team: z.string(), group: z.string().optional()});

type FormFields = z.infer<typeof scheme>;

const labelSx: StyleSx = {mb: 0.25};
const buttonSx: StyleSx = {my: 2, alignSelf: 'center'};
const footerSx: StyleSx = {mt: 'auto', mb: 2};

interface Props extends Sx {
  code: string;
  initTeamId: string;
  teams: Team.Team[];
}

export function QuickPairing({sx, code, initTeamId, teams}: Props) {
  const [groupDialog, setGroupDialog] = useState(false);
  const [processing, setProcessing] = useState(false);
  const [addSeat, setAddSeat] = useState(false);

  const {
    control,
    formState: {isValid},
    handleSubmit,
    setValue,
  } = useForm<FormFields>({
    defaultValues: {team: initTeamId, group: ''},
    mode: 'onChange',
    resolver: zodResolver(scheme),
  });

  const teamId = useWatch({control, name: 'team'});

  const checkingState = useCheckCode({code, teamId, enabled: true});
  const stats = useTeamStats(teamId);

  const singleTeam = teams.length === 1;

  const allowGroups = stats?.plan.premium ?? false;

  const permitPlanUpdate = stats?.user.role.canEditBilling() ?? false;

  const showSeatsSwitcher = checkingState === 'no-seats-expandable' && permitPlanUpdate;

  const isOkCode = checkingState === 'ok';

  const forceAddSeat = checkingState === 'no-seats-expandable' && addSeat;

  const readyToPair = isOkCode || forceAddSeat;

  const {groups, refetch} = useEdgeGroups({teamId, enabled: allowGroups ?? false});

  const handleCreateGroup = async (name: string) => {
    const newGroup = await DeviceGroupApiService.createGroup(teamId, name);
    await refetch();
    setValue('group', newGroup.id);
    setGroupDialog(false);
  };

  const handlePair = useCallback(
    async (teamId: string, groupId?: string) => {
      try {
        setProcessing(true);
        const res = await CloudApi.pairDevice(teamId, code, {groupId, addSeat: forceAddSeat});
        const deviceId = res.ID;

        if (teamId !== initTeamId) {
          router.go('deviceDetails', {deviceId});
        } else {
          router.goToTeam(teamId, router.url('deviceDetails', {deviceId}));
        }
      } finally {
        setProcessing(false);
      }
    },
    [code, initTeamId, forceAddSeat],
  );

  const handleForm = async (fields: FormFields) => handlePair(fields.team, fields.group);

  useEffect(() => {
    if (singleTeam && isOkCode) {
      void handlePair(teamId);
    }
  }, [singleTeam, isOkCode, teamId, handlePair]);

  useEffect(() => {
    if (!permitPlanUpdate) {
      setAddSeat(false);
    }
  }, [permitPlanUpdate]);

  const renderTeam = () => {
    if (singleTeam) {
      return null;
    }

    return (
      <>
        <FormLabel sx={labelSx}>Team</FormLabel>

        <Controller
          name="team"
          control={control}
          render={({field}) => (
            <Select
              {...field}
              sx={{mb: 3}}
              data-id="team-input"
              variant="standard"
              fullWidth={true}
              disabled={processing}
              displayEmpty={true}
              MenuProps={{sx: {maxHeight: 300, width: 380}}}
              renderValue={(selected) => {
                if (!selected) {
                  return <Typography sx={{color: 'text.disabled'}}>None</Typography>;
                }

                return teams.find((t) => t.id === selected)?.name ?? null;
              }}
              onChange={(e) => {
                setValue('group', '');
                field.onChange(e);
              }}
            >
              <MenuItem disabled={true} value="">
                Select team
              </MenuItem>

              {teams.map((t) => (
                <MenuItem data-id={`group-${t.id}`} key={t.id} value={t.id}>
                  <Typography variant="inherit" noWrap={true}>
                    {t.name}
                  </Typography>
                </MenuItem>
              ))}
            </Select>
          )}
        />
      </>
    );
  };

  const renderGroup = () => {
    if (allowGroups) {
      return (
        <>
          <CreateGroupDialog
            open={groupDialog}
            onCreate={handleCreateGroup}
            onClose={() => setGroupDialog(false)}
          />

          <Stack mb={0.25} flexDirection="row" justifyContent="space-between" alignItems="center">
            <FormLabel sx={{mb: 0}}>Device group</FormLabel>

            <Button
              sx={{py: 0}}
              variant="text"
              color="secondary"
              disabled={processing}
              startIcon={<AddIcon fontSize="small" />}
              onClick={() => setGroupDialog(true)}
            >
              Create new group
            </Button>
          </Stack>

          <Controller
            name="group"
            control={control}
            render={({field}) => (
              <Select
                {...field}
                sx={{mb: 3}}
                data-id="group-input"
                variant="standard"
                fullWidth={true}
                disabled={processing}
                displayEmpty={true}
                MenuProps={{sx: {maxHeight: 300, width: 380}}}
              >
                <MenuItem value="">
                  <Typography component="em" color="text.disabled">
                    None
                  </Typography>
                </MenuItem>

                {groups.map((v) => (
                  <MenuItem data-id={`group-${v.id}`} key={v.id} value={v.id}>
                    <Typography variant="inherit" noWrap={true}>
                      {v.name}
                    </Typography>
                  </MenuItem>
                ))}
              </Select>
            )}
          />
        </>
      );
    }

    return null;
  };

  return (
    <Stack sx={sx} alignItems="center">
      <Typography variant="h4" fontWeight={600} textAlign="center">
        Pair your device to your <br />
        Epiphan Cloud team account
      </Typography>

      <Stack mt={4} width={380} component="form" onSubmit={handleSubmit(handleForm)}>
        {renderTeam()}

        {renderGroup()}

        <ValidationMessage sx={{mb: 1}} state={checkingState} />

        {showSeatsSwitcher && (
          <FormControlLabel
            sx={{mt: 1, mb: 0, alignSelf: 'center'}}
            control={
              <Switch
                data-id="add-seat-switch"
                color="secondary"
                checked={addSeat}
                onChange={(e) => setAddSeat(e.target.checked)}
              />
            }
            label="Add one seat into the plan"
          />
        )}

        <LoadingButton
          sx={buttonSx}
          type="submit"
          variant="contained"
          color="secondary"
          loading={processing}
          disabled={!isValid || !readyToPair}
        >
          Pair device
        </LoadingButton>
      </Stack>

      {stats && (
        <SeatsSummary
          sx={footerSx}
          paired={stats.paired.devices}
          premium={stats.plan.premium}
          total={stats.plan.tier.seats}
          permitModifyPlan={stats.user.role.canEditBilling()}
        />
      )}
    </Stack>
  );
}

function useTeamStats(teamId: string) {
  const {data: plan} = useQuery({
    queryKey: ['edge', 'plan', teamId],
    queryFn: async () => {
      const res = await EdgeBillingApi.getEdgePlan(teamId);
      return BillingMapper.mapEdgePlan(res);
    },
  });
  const {data: paired} = usePairedStatsQuery({teamId});
  const {data: user} = useQuery({
    queryKey: ['pairing', 'profile', teamId],
    queryFn: async () => {
      const response = await UserApi.getTeamProfile(teamId);
      return UserMapper.mapProfile(response);
    },
  });

  if (paired && plan && user) {
    return {plan, paired, user};
  }

  return undefined;
}
